import { useQuery } from '@tanstack/react-query';
import { initSanityClient } from '../helpers/sanity';
import { useMarketplaceConfig } from '../queries/marketplace';
import { useTranslation } from 'react-i18next';
import {
  ALL_PROMO_PRODUCT_AVAILABILITIES,
  filterPromoContentByProductAvailability,
  filterPromoTagsByProductAvailability,
} from '../utils/promotions';
import type { SanityPromoContentItem, SanityPromoTag } from '../@types';

export type Content =
  | 'PromotionalTopContent'
  | 'HomePromotionalTopContent'
  | 'PromotionalSideContent'
  | 'PortableTextContent'
  | 'FooterAboutUsContent'
  | 'FooterPolicyContent'
  | 'FooterPromoContent'
  | 'FooterSupportContent'
  | 'CasinoPromotionalTopContent'
  | 'CasinoBackgroundImage'
  | 'Logo'
  | 'PromoContent'
  | 'PromoTag';

const QUERY_KEY_RECORD: Record<Content, string> = {
  PromotionalTopContent: 'top-data',
  HomePromotionalTopContent: 'home-top-data',
  PromotionalSideContent: 'side-data',
  PortableTextContent: 'footer-data',
  FooterAboutUsContent: 'footer-about-us-data',
  FooterPolicyContent: 'footer-policy-data',
  FooterPromoContent: 'footer-promo-data',
  FooterSupportContent: 'footer-support-data',
  CasinoPromotionalTopContent: 'top-data-casino',
  CasinoBackgroundImage: 'background-image-casino',
  Logo: 'logo',
  PromoContent: 'promo-content',
  PromoTag: 'promo-tags',
};

const LOCALIZED_FIELDS = [
  'title',
  'subtitle',
  'buttonText',
  'infoText',
  'callToActionTitle',
  'upperText',
  'shortDescription',
  'duration',
] as const;

export interface SanityClient {
  fetch: <T>(query: string, params?: Record<string, unknown>) => Promise<T>;
}

export const useSanityClient = (): SanityClient | undefined => {
  const { data: marketplaceConfig } = useMarketplaceConfig();

  if (!marketplaceConfig?.sanityConfig?.projectId || !marketplaceConfig?.sanityConfig?.dataset) {
    return undefined;
  }

  return (
    initSanityClient(marketplaceConfig.sanityConfig.projectId, marketplaceConfig.sanityConfig.dataset) ?? undefined
  );
};

export const createSanityLocalizedProjection = (language: string) =>
  LOCALIZED_FIELDS.map((field) => `${field}, "${field}": coalesce(${field}.${language}, ${field}.en, ${field})`).join(
    ', '
  );

export const useSanity = <T>(contentType: Content, pathname?: string, enabled = true) => {
  const client = useSanityClient();
  const type = `_type=="${contentType}"`;
  const path = pathname ? `&& path == "${pathname.split('/')[2]}"` : '';
  const { i18n } = useTranslation();

  // Build GROQ projection for localized fields: use coalesce to fallback to en locale or whole field if no localized version exists
  const localizedProjection = createSanityLocalizedProjection(i18n.language);

  // Construct the GROQ query to fetch all fields, including localized projections
  const query = `*[
    ${type}
    ${path}
  ]{
    ...,
    ${localizedProjection},
    "body": coalesce(body.${i18n.language}, body.en)
  }`;

  return useQuery<T[]>({
    queryKey: [QUERY_KEY_RECORD[contentType], pathname || '', i18n.language],
    queryFn: () => {
      if (!client) return Promise.resolve([]);
      return client.fetch(query);
    },
    enabled: !!client && enabled,
    staleTime: Infinity,
  });
};

const promoTagProjection = `
  _id,
  isSystem,
  "productAvailability": coalesce(productAvailability, "both"),
  "label": coalesce(label[$language], label.en, ""),
  "filterValue": _id,
  "bgColor": bgColor.hex,
  "textColor": textColor.hex,
  "borderColor": borderColor.hex
`;

const promoContentProjection = `
  _id,
  _createdAt,
  image,
  "title": coalesce(title[$language], title.en, title),
  "shortDescription": coalesce(shortDescription[$language], shortDescription.en, shortDescription),
  "duration": coalesce(duration[$language], duration.en, duration),
  "infoText": coalesce(infoText[$language], infoText.en, infoText),
  "tags": tags[]->{
    ${promoTagProjection}
  }
`;

export const usePromoTags = (allowedProductAvailabilities = ALL_PROMO_PRODUCT_AVAILABILITIES, enabled = true) => {
  const client = useSanityClient();
  const { i18n } = useTranslation();
  const query = `*[_type == "PromoTag"] | order(isSystem desc, label.en asc){${promoTagProjection}}`;

  return useQuery<SanityPromoTag[]>({
    queryKey: ['promo-tags', i18n.language],
    queryFn: () => {
      if (!client) return Promise.resolve([]);
      return client.fetch(query, { language: i18n.language });
    },
    select: (tags) => filterPromoTagsByProductAvailability(tags, allowedProductAvailabilities),
    enabled: !!client && enabled && allowedProductAvailabilities.length > 0,
    staleTime: Infinity,
  });
};

export const usePromoContentByTag = (
  tag?: SanityPromoTag,
  allowedProductAvailabilities = ALL_PROMO_PRODUCT_AVAILABILITIES,
  enabled = true
) => {
  const client = useSanityClient();
  const { i18n } = useTranslation();
  const selectedTagId = tag?.isSystem ? undefined : tag?._id;
  const filter = selectedTagId ? ' && references($tagId)' : '';
  const query = `*[_type == "PromoContent"${filter}] | order(_createdAt desc){${promoContentProjection}}`;

  return useQuery<SanityPromoContentItem[]>({
    queryKey: ['promo-content', selectedTagId || 'all', i18n.language],
    queryFn: () => {
      if (!client) return Promise.resolve([]);
      const params = selectedTagId ? { language: i18n.language, tagId: selectedTagId } : { language: i18n.language };

      return client.fetch(query, params);
    },
    select: (promotions) => filterPromoContentByProductAvailability(promotions, allowedProductAvailabilities),
    enabled: !!client && enabled && !!tag && allowedProductAvailabilities.length > 0,
    staleTime: Infinity,
  });
};
